import React, { useEffect, useState } from 'react';
import ModalBody from '../modal/ModalBody';
import ModalFooter from '../modal/ModalFooter';
import ModalHeader from '../modal/ModalHeader';

export type ConfirmDialogOptions = {
  title: string,
  message: React.ReactNode,
  confirmLabel: string,
  cancelLabel?: string,
  danger?: boolean,
  input?: { label: string, defaultValue: string, maxLength?: number },
  onConfirm: (value: string) => void,
};

type Props = ConfirmDialogOptions & {
  onClose: () => void,
};

// In-page replacement for window.confirm / window.prompt.
const ConfirmDialog = (props: Props): React.ReactElement => {
  const {
    title, message, confirmLabel, cancelLabel, danger, input, onConfirm, onClose,
  } = props;
  const [value, setValue] = useState(input?.defaultValue || '');

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onConfirm(value.trim());
    onClose();
  };

  return (
    <div className="modal show d-block desk-confirm" tabIndex={-1} role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" role="document" onClick={(event) => event.stopPropagation()}>
        <form className="modal-content" onSubmit={handleSubmit}>
          <ModalHeader title={title} onCloseClick={onClose} />
          <ModalBody>
            <p className="mb-0">{message}</p>
            {input && (
              <div className="form-group mt-3 mb-0">
                <label htmlFor="desk-confirm-input">{input.label}</label>
                <input
                  id="desk-confirm-input"
                  className="form-control"
                  value={value}
                  maxLength={input.maxLength}
                  onChange={(event) => setValue(event.target.value)}
                  autoFocus
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{cancelLabel || 'Cancel'}</button>
            <button
              type="submit"
              className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
              disabled={!!input && !value.trim()}
              autoFocus={!input}
            >
              {confirmLabel}
            </button>
          </ModalFooter>
        </form>
      </div>
    </div>
  );
};

export default ConfirmDialog;
